import React from 'react'
import ReactDOM from 'react-dom/client'
import { createBrowserRouter, RouterProvider, Link } from 'react-router-dom'
import { HelmetProvider } from 'react-helmet-async'
import { AuthProvider } from '@/contexts/AuthContext'
import { ThemeProvider } from '@/components/ui/theme-provider'
import { GoogleTagManager } from '@/components/analytics/GoogleTagManager'
import { MarketingLayout } from '@/components/marketing/MarketingLayout'
import { DashboardLayout } from '@/components/dashboard/DashboardLayout'
import Home from '@/pages/Home'
import About from '@/pages/About'
import Features from '@/pages/Features'
import Login from '@/pages/Login'
import Register from '@/pages/Register'
import Pricing from '@/pages/Pricing'
import Contact from '@/pages/Contact'
import Blog from '@/pages/Blog'
import BlogPost from '@/pages/BlogPost'
import Overview from '@/pages/dashboard/Overview'
import Profile from '@/pages/dashboard/Profile'
import Settings from '@/pages/dashboard/Settings'
import PrivacyPolicy from '@/pages/PrivacyPolicy'
import TermsAndConditions from '@/pages/TermsAndConditions'
import { ProtectedRoute } from '@/components/ProtectedRoute'
import { registerServiceWorker } from './pwa'
import './index.css'

const router = createBrowserRouter([
  {
    element: <MarketingLayout />,
    children: [
      {
        path: "/",
        element: <Home />,
      },
      {
        path: "/about",
        element: <About />,
      },
      {
        path: "/features",
        element: <Features />,
      },
      {
        path: "/login",
        element: <Login />,
      },
      {
        path: "/register",
        element: <Register />,
      },
      {
        path: "/pricing",
        element: <Pricing />,
      },
      {
        path: "/contact",
        element: <Contact />,
      },
      {
        path: "/blog",
        element: <Blog />,
      },
      {
        // Route for blog posts with two-segment pattern (category/slug)
        path: "/:category/:slug",
        element: <BlogPost />,
      },
      {
        // Route for blog posts with three-segment pattern (category/subcategory/slug)
        path: "/:category/:subcategory/:slug",
        element: <BlogPost />,
      },
      {
        path: "/privacy-policy",
        element: <PrivacyPolicy />,
      },
      {
        path: "/terms-and-conditions",
        element: <TermsAndConditions />,
      },
      {
        // Fallback route for any other paths
        path: "*",
        element: <div className="container mx-auto px-4 py-12 text-center">
          <h1 className="text-3xl font-bold mb-4">Page Not Found</h1>
          <p className="mb-8">The page you're looking for doesn't exist or has been moved.</p>
          <Link to="/" className="text-primary hover:underline">Go back to home</Link>
        </div>,
      },
    ],
  },
  {
    element: <ProtectedRoute><DashboardLayout /></ProtectedRoute>,
    children: [
      {
        path: "/dashboard",
        element: <Overview />,
      },
      {
        path: "/dashboard/profile",
        element: <Profile />,
      },
      {
        path: "/dashboard/settings",
        element: <Settings />,
      },
    ],
  },
])

// Get Google Tag Manager ID from environment variables
const gtmId = import.meta.env.VITE_GTM_ID || 'GTM-WL4TXJRW'

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ThemeProvider attribute="class" defaultTheme="system" enableSystem disableTransitionOnChange>
      <HelmetProvider>
        <GoogleTagManager id={gtmId} />
        <AuthProvider>
          <RouterProvider router={router} />
        </AuthProvider>
      </HelmetProvider>
    </ThemeProvider>
  </React.StrictMode>,
)

// Register service worker for PWA functionality
registerServiceWorker()